import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { NavigateBefore as NavigateBeforeIcon, NavigateNext as NavigateNextIcon } from "@mui/icons-material";

import { axiosPrivate } from "../../../../api/axios";

/* ── Domain theme with raw color values for advanced effects ── */
const DOMAIN_THEME = {
  finance: {
    accent: "#60a5fa",
    accentRgb: "96,165,250",
    labelColor: "#bfdbfe",
    badgeBg: "rgba(59,130,246,0.12)",
    badgeBorder: "rgba(96,165,250,0.25)",
    iconChar: "F",
  },
  logistics: {
    accent: "#5eead4",
    accentRgb: "94,234,212",
    labelColor: "#ccfbf1",
    badgeBg: "rgba(20,184,166,0.12)",
    badgeBorder: "rgba(94,234,212,0.25)",
    iconChar: "L",
  },
  quality: {
    accent: "#fbbf24",
    accentRgb: "251,191,36",
    labelColor: "#fef3c7",
    badgeBg: "rgba(245,158,11,0.12)",
    badgeBorder: "rgba(251,191,36,0.25)",
    iconChar: "Q",
  },
  inventory: {
    accent: "#34d399",
    accentRgb: "52,211,153",
    labelColor: "#d1fae5",
    badgeBg: "rgba(16,185,129,0.12)",
    badgeBorder: "rgba(52,211,153,0.25)",
    iconChar: "S",
  },
};

const FALLBACK_THEME = {
  accent: "#94a3b8",
  accentRgb: "148,163,184",
  labelColor: "#cbd5e1",
  badgeBg: "rgba(100,116,139,0.12)",
  badgeBorder: "rgba(148,163,184,0.2)",
  iconChar: "—",
};

const formatAsOf = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function TopKpiSlider() {
  const { group_code, project_code } = useParams();
  const [slides, setSlides] = useState([]);
  const [asOfDatetime, setAsOfDatetime] = useState(null);
  const [startIndex, setStartIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [autoSlideEnabled, setAutoSlideEnabled] = useState(true);

  const cards = useMemo(
    () =>
      slides.flatMap((slide) =>
        (slide.metrics || []).map((metric, idx) => ({
          id: `${slide.id || "slide"}-${metric.label}-${idx}`,
          domain: slide.id || "unknown",
          label: metric.label,
          prev: metric.prev,
          mtd: metric.mtd,
          row1Label: metric.row1Label,
          row2Label: metric.row2Label,
          status: metric.status,
        }))
      ),
    [slides]
  );

  useEffect(() => {
    const fetchKpiSnapshot = async () => {
      try {
        const response = await axiosPrivate.post("/dt/getKpiSnapshot", {
          group_code,
          project_code,
        });

        const nextSlides = Array.isArray(response.data?.slides) ? response.data.slides : [];
        setSlides(nextSlides);
        setAsOfDatetime(response.data?.asOfDatetime || null);
        setStartIndex(0);
      } catch (error) {
        console.error("Error fetching KPI snapshot:", error);
        setSlides([]);
      }
    };

    fetchKpiSnapshot();
  }, [group_code, project_code]);

  useEffect(() => {
    if (!autoSlideEnabled || isHovered || cards.length <= 1) return undefined;

    const timer = setInterval(() => {
      setStartIndex((prev) => (prev + 1) % cards.length);
    }, 8000);

    return () => clearInterval(timer);
  }, [autoSlideEnabled, isHovered, cards.length]);

  useEffect(() => {
    if (startIndex < cards.length) return;
    setStartIndex(0);
  }, [startIndex, cards.length]);

  const visibleCards = useMemo(() => {
    if (cards.length === 0) return [];
    const visibleCount = Math.min(4, cards.length);
    return Array.from({ length: visibleCount }, (_, idx) => cards[(startIndex + idx) % cards.length]);
  }, [cards, startIndex]);

  if (visibleCards.length === 0) return null;

  const goNext = () => setStartIndex((prev) => (prev + 1) % cards.length);
  const goPrev = () => setStartIndex((prev) => (prev - 1 + cards.length) % cards.length);

  return (
    <div
      className="absolute top-10 left-1/2 -translate-x-1/2 w-[calc(100%-110px)] max-w-[800px] z-40 pointer-events-none"
      style={{ fontFamily: "'Pretendard', -apple-system, sans-serif" }}
    >
      <div
        className="pointer-events-auto"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative overflow-visible">
          <div className="relative py-1">
            {/* ── Header bar ── */}
            <div className="flex items-center justify-end gap-2.5 mb-2.5 pr-0.5">
              <span
                style={{
                  fontSize: 10,
                  color: "rgba(148,163,184,0.8)",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                기준 {formatAsOf(asOfDatetime)}
              </span>
              <button
                type="button"
                onClick={() => setAutoSlideEnabled((prev) => !prev)}
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  padding: "3px 10px",
                  borderRadius: 6,
                  border: `1px solid ${autoSlideEnabled ? "rgba(96,165,250,0.3)" : "rgba(255,255,255,0.12)"}`,
                  color: autoSlideEnabled ? "#93c5fd" : "rgba(148,163,184,0.7)",
                  background: autoSlideEnabled ? "rgba(59,130,246,0.08)" : "transparent",
                  cursor: "pointer",
                  fontFamily: "'Pretendard', monospace",
                  transition: "background 0.15s ease",
                }}
              >
                {autoSlideEnabled ? "● AUTO" : "○ AUTO"}
              </button>
            </div>

            {/* ── Card area with nav ── */}
            <div className="relative">
              {cards.length > 1 && (
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute -left-4 md:-left-10 top-1/2 -translate-y-1/2 w-8 h-8 md:w-9 md:h-9 rounded-full bg-transparent border border-transparent text-white/70 hover:text-white hover:bg-white/12 hover:border-white/25 transition-colors flex items-center justify-center z-20"
                  aria-label="이전 KPI"
                >
                  <NavigateBeforeIcon sx={{ fontSize: 22 }} />
                </button>
              )}

              {/* ── Card grid ── */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                {visibleCards.map((card) => {
                  const theme = DOMAIN_THEME[card.domain] || FALLBACK_THEME;
                  return (
                    <div
                      key={card.id}
                      style={{
                        position: "relative",
                        borderRadius: 14,
                        overflow: "hidden",
                        cursor: "default",
                      }}
                    >
                      {/* ── Card body ── */}
                      <div
                        style={{
                          position: "relative",
                          padding: "12px 14px 13px",
                          borderRadius: 14,
                          background: "rgba(15,23,42,0.72)",
                          border: `1px solid rgba(${theme.accentRgb},0.15)`,
                          boxShadow: `
                            0 0 0 1px rgba(0,0,0,0.3),
                            0 1px 2px rgba(0,0,0,0.3),
                            0 8px 24px rgba(0,0,0,0.25),
                            inset 0 1px 0 rgba(255,255,255,0.04)
                          `,
                        }}
                      >
                        {/* Top accent line */}
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 16,
                            right: 16,
                            height: 1,
                            background: `linear-gradient(90deg, transparent, rgba(${theme.accentRgb},0.5), transparent)`,
                          }}
                        />

                        {/* ── Domain badge ── */}
                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
                          <div
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: 6,
                              background: theme.badgeBg,
                              border: `1px solid ${theme.badgeBorder}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              position: "relative",
                            }}
                          >
                            <span
                              style={{
                                fontSize: 9,
                                fontWeight: 900,
                                color: theme.accent,
                                letterSpacing: "0.02em",
                                lineHeight: 1,
                              }}
                            >
                              {theme.iconChar}
                            </span>
                          </div>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 800,
                              color: theme.labelColor,
                              letterSpacing: "0.04em",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              flex: 1,
                            }}
                          >
                            {card.label}
                          </span>
                          {/* Status dot */}
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: card.status === "warning" ? "#fbbf24"
                                : card.status === "danger" ? "#f87171"
                                : theme.accent,
                              boxShadow: `0 0 6px rgba(${theme.accentRgb},0.5)`,
                              flexShrink: 0,
                            }}
                          />
                        </div>

                        {/* ── Metric rows ── */}
                        <div
                          style={{
                            borderTop: "1px solid rgba(255,255,255,0.06)",
                            paddingTop: 9,
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          {/* Row 1 - Previous */}
                          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6 }}>
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: "rgba(148,163,184,0.6)",
                                letterSpacing: "0.04em",
                                flexShrink: 0,
                              }}
                            >
                              {card.row1Label || "전월"}
                            </span>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: "rgba(226,232,240,0.85)",
                                fontVariantNumeric: "tabular-nums",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: "right",
                              }}
                            >
                              {card.prev}
                            </span>
                          </div>

                          {/* Row 2 - Current (MTD) — emphasized */}
                          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6 }}>
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: `rgba(${theme.accentRgb},0.55)`,
                                letterSpacing: "0.04em",
                                flexShrink: 0,
                              }}
                            >
                              {card.row2Label || "당월"}
                            </span>
                            <span
                              style={{
                                fontSize: 14,
                                fontWeight: 800,
                                color: theme.labelColor,
                                fontVariantNumeric: "tabular-nums",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: "right",
                                textShadow: `0 0 12px rgba(${theme.accentRgb},0.25)`,
                              }}
                            >
                              {card.mtd}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {cards.length > 1 && (
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute -right-4 md:-right-10 top-1/2 -translate-y-1/2 w-8 h-8 md:w-9 md:h-9 rounded-full bg-transparent border border-transparent text-white/70 hover:text-white hover:bg-white/12 hover:border-white/25 transition-colors flex items-center justify-center z-20"
                  aria-label="다음 KPI"
                >
                  <NavigateNextIcon sx={{ fontSize: 22 }} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
